import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Loader2, Plus, Trash2, Edit2, ListTree, X, Copy } from "lucide-react";

export default function SubCategoriasTab({ categorias, getAuthHeaders }) {
  const [reglas, setReglas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ categoria_id: "", departamento_id: "all", subcategorias: [] });
  const [subInput, setSubInput] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [reglasRes, deptRes, prioRes] = await Promise.all([
        axios.get(`${API}/tickets/subcategorias-reglas`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/tickets/prioridades`, getAuthHeaders()),
      ]);
      setReglas(reglasRes.data);
      setDepartamentos(deptRes.data);
      setPrioridades(prioRes.data || []);
    } catch (err) {
      toast.error("Error al cargar las reglas de subcategorías");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const setSubPrioridad = async (reglaId, subcategoria, prioridadId) => {
    try {
      await axios.put(
        `${API}/tickets/subcategorias-reglas/${reglaId}/prioridad`,
        { subcategoria, prioridad_id: prioridadId || null },
        getAuthHeaders()
      );
      // Actualización optimista local para evitar recarga completa
      setReglas((prev) =>
        prev.map((r) => {
          if (r.id !== reglaId) return r;
          const sp = { ...(r.subcategorias_prioridades || {}) };
          if (prioridadId) {
            const prio = prioridades.find((p) => p.id === prioridadId);
            sp[subcategoria] = { prioridad_id: prioridadId, prioridad_name: prio?.name };
          } else {
            delete sp[subcategoria];
          }
          return { ...r, subcategorias_prioridades: sp };
        })
      );
      toast.success("Prioridad de la subcategoría actualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al asignar la prioridad");
    }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => {
    setForm({ categoria_id: "", departamento_id: "all", subcategorias: [] });
    setSubInput("");
    setEditingId(null);
  };

  const addSub = () => {
    const v = subInput.trim();
    if (!v) return;
    if (form.subcategorias.includes(v)) { setSubInput(""); return; }
    setForm((p) => ({ ...p, subcategorias: [...p.subcategorias, v] }));
    setSubInput("");
  };

  const removeSub = (s) => setForm((p) => ({ ...p, subcategorias: p.subcategorias.filter((x) => x !== s) }));

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      categoria_id: r.categoria_id,
      departamento_id: r.departamento_id || "all",
      subcategorias: r.subcategorias || [],
    });
  };

  const startClone = (r) => {
    // Clona la regla en el formulario SIN editingId, para crear una nueva
    // copia que el usuario solo tenga que ajustar.
    setEditingId(null);
    setForm({
      categoria_id: r.categoria_id,
      departamento_id: r.departamento_id || "all",
      subcategorias: [...(r.subcategorias || [])],
    });
    setSubInput("");
    toast.info("Regla clonada en el formulario. Ajústala y pulsa 'Crear regla'.");
  };

  const handleSave = async () => {
    if (!form.categoria_id) { toast.error("Selecciona un Departamento a asignar"); return; }
    if (form.subcategorias.length === 0) { toast.error("Agrega al menos una subcategoría"); return; }
    const payload = {
      categoria_id: form.categoria_id,
      departamento_id: form.departamento_id === "all" ? null : form.departamento_id,
      subcategorias: form.subcategorias,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/tickets/subcategorias-reglas/${editingId}`, payload, getAuthHeaders());
        toast.success("Regla actualizada");
      } else {
        await axios.post(`${API}/tickets/subcategorias-reglas`, payload, getAuthHeaders());
        toast.success("Regla creada");
      }
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar la regla");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta regla de subcategorías?")) return;
    try {
      await axios.delete(`${API}/tickets/subcategorias-reglas/${id}`, getAuthHeaders());
      toast.success("Regla eliminada");
      fetchAll();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const catName = (id) => categorias.find((c) => c.id === id)?.name || "—";
  const deptName = (id) => {
    if (!id) return "Todos los departamentos";
    const d = departamentos.find((x) => x.id === id);
    return d ? (d.nombre || d.name) : id;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="subcategorias-tab">
      {/* Formulario de regla */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <ListTree className="w-5 h-5 text-[#0C314C]" />
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {editingId ? "Editar regla" : "Nueva regla de subcategorías"}
            </h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Configura: <strong>si</strong> se selecciona este <strong>Departamento a asignar</strong>, mostrar estas
            subcategorías, <strong>pero solo si</strong> el usuario que crea el ticket pertenece al departamento indicado.
          </p>

          <div className="space-y-2">
            <Label>Departamento a asignar *</Label>
            <Select value={form.categoria_id} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
              <SelectTrigger data-testid="subcat-categoria-select">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {categorias.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Departamento del usuario (condición)</Label>
            <Select value={form.departamento_id} onValueChange={(v) => setForm({ ...form, departamento_id: v })}>
              <SelectTrigger data-testid="subcat-departamento-select">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los departamentos</SelectItem>
                {departamentos.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.nombre || d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Subcategorías</Label>
            <div className="flex gap-2">
              <Input
                value={subInput}
                onChange={(e) => setSubInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
                placeholder="Ej: Emisión, Minuta Reunión..."
                data-testid="subcat-input"
              />
              <Button type="button" variant="outline" onClick={addSub} data-testid="subcat-add-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.subcategorias.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {form.subcategorias.map((s) => (
                  <Badge key={s} variant="secondary" className="flex items-center gap-1">
                    {s}
                    <button onClick={() => removeSub(s)} className="ml-1"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white flex-1" data-testid="subcat-save-btn">
              {editingId ? "Actualizar regla" : "Crear regla"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={resetForm}>Cancelar</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Listado de reglas */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900 dark:text-white">Reglas configuradas</h3>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : reglas.length === 0 ? (
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardContent className="py-10 text-center text-slate-500 dark:text-slate-400">
              <ListTree className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              Aún no hay reglas de subcategorías.
            </CardContent>
          </Card>
        ) : (
          reglas.map((r) => (
            <Card key={r.id} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800" data-testid={`subcat-regla-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {r.categoria_name || catName(r.categoria_id)}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Si el usuario pertenece a: <strong>{r.departamento_name || deptName(r.departamento_id)}</strong>
                    </p>
                    <div className="flex flex-col gap-2 mt-2">
                      {(r.subcategorias || []).map((s) => {
                        const sp = (r.subcategorias_prioridades || {})[s];
                        const currentPrioId = sp?.prioridad_id || "none";
                        return (
                          <div key={s} className="flex items-center justify-between gap-2 bg-slate-50 dark:bg-slate-700/40 rounded-md px-2 py-1.5">
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{s}</span>
                            <Select
                              value={currentPrioId}
                              onValueChange={(v) => setSubPrioridad(r.id, s, v === "none" ? null : v)}
                            >
                              <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`subcat-prio-select-${r.id}-${s}`}>
                                <SelectValue placeholder="Prioridad" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Por defecto (depto.)</SelectItem>
                                {prioridades.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name}{p.sla_hours != null ? ` · SLA ${p.sla_hours}h` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(r)} title="Editar" data-testid={`subcat-edit-${r.id}`}>
                      <Edit2 className="w-4 h-4 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startClone(r)} title="Clonar" data-testid={`subcat-clone-${r.id}`}>
                      <Copy className="w-4 h-4 text-blue-400" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} title="Eliminar" data-testid={`subcat-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
